/**
 * The AI mission operator's conversation, and the one proposal under review.
 *
 * A store rather than component state for the same reason missionStore is one:
 * the proposal has two consumers that never share a React tree. The dock reads
 * it to render the review gate; the map layer reads it — without React — to
 * draw the proposed route next to the draft and the uploaded mission. A
 * proposal held in a `useState` inside the dock is a proposal the map cannot
 * draw, and a route an operator approves without having seen on the map is
 * exactly the failure this whole screen exists to prevent.
 *
 * What this store does NOT do is decide anything. It records what the model
 * said, what tools it ran, and what the server graded the result. Approval is a
 * server call made from a deliberate human action, and `activeProposal` is
 * always replaced from the server's response so the status shown is the status
 * stored, never an optimistic guess.
 */

import { create } from 'zustand';

import {
  approveProposal,
  copilotTurnStream,
  executeProposal,
  getProposal,
  rejectProposal,
  undoProposal,
  type CopilotLiveStep,
  type CopilotStep,
  type CopilotTurn,
  type StoredProposal,
} from '@/api/copilot';
import type { DroneId, Waypoint } from '@/types/domain';

export interface CopilotMessage {
  role: 'user' | 'assistant';
  content: string;
  /** The tools this turn ran, in order. Provenance for the reviewer. */
  steps?: CopilotStep[];
  /** Set when the turn came back as a failure or the call itself failed. */
  error?: boolean;
  /** Which proposal this turn produced, if any. */
  proposalId?: string;
}

/** What the map draws. Same shape as MissionMap so the layer needs no adapter. */
export type ProposedRoutes = Record<DroneId, Waypoint[]>;

const NO_ROUTES: ProposedRoutes = {};

interface CopilotState {
  conversationId: string | null;
  messages: CopilotMessage[];
  activeProposal: StoredProposal | null;
  busy: boolean;
  /**
   * Tools the turn in flight has run so far, in order.
   *
   * Separate from the message list because they belong to a turn that has no
   * message yet: a turn takes two to four minutes, and the whole point of
   * carrying them is that the operator sees the work before the answer. Emptied
   * the moment the turn lands, where the same steps arrive attached to the
   * assistant message and become part of the reviewable trace.
   */
  liveSteps: CopilotLiveStep[];
  /** Operator-readable. A 503 "no model configured" lands here, not in a throw. */
  error: string | null;

  send: (message: string) => Promise<void>;
  refreshProposal: (proposalId: string) => Promise<void>;
  approve: (note?: string, override?: boolean) => Promise<{ ok: boolean; message: string }>;
  reject: (note?: string) => Promise<{ ok: boolean; message: string }>;
  execute: (confirmCallsigns?: string[]) => Promise<{ ok: boolean; message: string }>;
  undo: () => Promise<{ ok: boolean; message: string }>;
  setActiveProposal: (proposal: StoredProposal | null) => void;
  reset: () => void;
}

export const useCopilotStore = create<CopilotState>((set, get) => ({
  conversationId: null,
  messages: [],
  activeProposal: null,
  busy: false,
  liveSteps: [],
  error: null,

  send: async (message) => {
    const trimmed = message.trim();
    if (!trimmed || get().busy) return;
    const { conversationId, activeProposal } = get();
    set((state) => ({
      busy: true,
      error: null,
      liveSteps: [],
      messages: [...state.messages, { role: 'user', content: trimmed }],
    }));

    // The end state is written in exactly two places, and both of them clear
    // `busy` and `liveSteps`. A stream that ended without either would leave a
    // dock the operator cannot use, which is a worse hang than the spinner this
    // replaces.
    const landed = (turn: CopilotTurn): void => {
      set((state) => ({
        busy: false,
        liveSteps: [],
        // Deliberately NOT turn.failure. That field is the parse error — "Reply
        // was not valid JSON (Extra data). (2 unreadable replies this turn.)" —
        // and it was being printed to the operator in the banner on top of the
        // assistant message, which already explains the same failure in words
        // they can act on and is already styled as an error. The technical
        // string stays on the wire and in the server log, where an engineer
        // telling a sloppy model from a broken parser will look for it.
        //
        // The banner still exists for the case it was written for: a call that
        // never produced a turn at all, where there is no message to carry the
        // explanation.
        error: null,
        // Taken from the turn, not the proposal: a turn that only answered a
        // question produces no proposal, and losing the id there would start
        // the next message on a fresh thread.
        conversationId: turn.conversationId ?? state.conversationId,
        activeProposal: turn.proposal ?? state.activeProposal,
        messages: [
          ...state.messages,
          {
            role: 'assistant',
            content: turn.say,
            steps: turn.steps,
            ...(turn.failure ? { error: true } : {}),
            ...(turn.proposal ? { proposalId: turn.proposal.proposalId } : {}),
          },
        ],
      }));
    };

    await copilotTurnStream(
      {
        message: trimmed,
        ...(conversationId ? { conversationId } : {}),
        // Carrying the proposal id makes a follow-up a revision of what is on
        // the map rather than a second unrelated plan competing with it.
        ...(activeProposal ? { proposalId: activeProposal.proposalId } : {}),
      },
      {
        onStep: (step) => set((state) => ({ liveSteps: [...state.liveSteps, step] })),
        onDone: landed,
        onError: (error) =>
          set((state) => ({
            busy: false,
            liveSteps: [],
            error: error.message,
            messages: [
              ...state.messages,
              { role: 'assistant', content: error.message, error: true },
            ],
          })),
      },
    );
  },

  refreshProposal: async (proposalId) => {
    const result = await getProposal(proposalId);
    if (result.ok) set({ activeProposal: result.data, error: null });
    else set({ error: result.error.message });
  },

  approve: async (note, override) => {
    const proposal = get().activeProposal;
    if (!proposal) return { ok: false, message: 'No proposal is under review.' };
    set({ busy: true, error: null });
    // The hash of what the reviewer actually read. A 409 means it changed, and
    // that is a finding to surface, not a retry to paper over.
    const result = await approveProposal(proposal.proposalId, proposal.contentHash, {
      ...(note ? { note } : {}),
      ...(override ? { overrideNoGo: override } : {}),
    });
    if (!result.ok) {
      const message =
        result.error.kind === 'conflict'
          ? `${result.error.message} — the proposal changed since you read it. Re-read it before approving.`
          : result.error.message;
      set({ busy: false, error: message });
      return { ok: false, message };
    }
    set({ busy: false, activeProposal: result.data });
    return { ok: true, message: 'Approved.' };
  },

  reject: async (note) => {
    const proposal = get().activeProposal;
    if (!proposal) return { ok: false, message: 'No proposal is under review.' };
    set({ busy: true, error: null });
    const result = await rejectProposal(proposal.proposalId, note);
    if (!result.ok) {
      set({ busy: false, error: result.error.message });
      return { ok: false, message: result.error.message };
    }
    set({ busy: false, activeProposal: result.data });
    return { ok: true, message: 'Rejected.' };
  },

  execute: async (confirmCallsigns) => {
    const proposal = get().activeProposal;
    if (!proposal) return { ok: false, message: 'No proposal is under review.' };
    set({ busy: true, error: null });
    const result = await executeProposal(proposal.proposalId, confirmCallsigns);
    if (!result.ok) {
      set({ busy: false, error: result.error.message });
      return { ok: false, message: result.error.message };
    }
    // A 200 is not the same as "every aircraft took it". The report's own `ok`
    // is the one that says whether this was a full upload or a partial, and a
    // partial reported as success is an operator believing a wingman is flying
    // a route it never received.
    const { proposal: updated, report } = result.data;
    set({ busy: false, activeProposal: updated, error: report.ok ? null : report.message });
    return { ok: report.ok, message: report.message || 'Uploaded to the fleet.' };
  },

  undo: async () => {
    const proposal = get().activeProposal;
    if (!proposal) return { ok: false, message: 'No proposal is under review.' };
    set({ busy: true, error: null });
    const result = await undoProposal(proposal.proposalId);
    if (!result.ok) {
      set({ busy: false, error: result.error.message });
      return { ok: false, message: result.error.message };
    }
    // Best effort by construction: an aircraft already flying the new route
    // cannot be un-flown by re-uploading the old one, and the report says so.
    const { proposal: updated, report } = result.data;
    set({ busy: false, activeProposal: updated, error: report.ok ? null : report.message });
    return { ok: report.ok, message: report.message || 'Rolled back.' };
  },

  setActiveProposal: (proposal) => set({ activeProposal: proposal }),

  reset: () =>
    set({
      conversationId: null,
      messages: [],
      activeProposal: null,
      busy: false,
      liveSteps: [],
      error: null,
    }),
}));

/**
 * The routes the map should draw as PROPOSED.
 *
 * Only a proposal still awaiting a decision draws. Once it is approved the
 * routes belong to the draft/uploaded story, and leaving a rejected or
 * superseded revision on the map is how two plans end up looking equally live.
 */
export function proposedRoutes(proposal: StoredProposal | null): ProposedRoutes {
  if (!proposal) return NO_ROUTES;
  if (proposal.status !== 'proposed' && proposal.status !== 'approved') return NO_ROUTES;
  return proposal.proposal.routes;
}

/**
 * Watch the proposed geometry without React, the way the mission layer does.
 * Fires on identity change of the active proposal only — a busy flag or a new
 * chat message must not redraw the map.
 */
export function subscribeToProposedMission(
  callback: (routes: ProposedRoutes) => void,
): () => void {
  let previous = useCopilotStore.getState().activeProposal;
  callback(proposedRoutes(previous));

  return useCopilotStore.subscribe((state) => {
    if (state.activeProposal === previous) return;
    previous = state.activeProposal;
    callback(proposedRoutes(previous));
  });
}

/**
 * Actions at module scope, stable forever. A component that reaches one action
 * through `useCopilotStore()` has subscribed to every field in this file.
 */
export const copilotActions = {
  send: (message: string) => useCopilotStore.getState().send(message),
  refreshProposal: (proposalId: string) => useCopilotStore.getState().refreshProposal(proposalId),
  approve: (note?: string, override?: boolean) =>
    useCopilotStore.getState().approve(note, override),
  reject: (note?: string) => useCopilotStore.getState().reject(note),
  execute: (confirmCallsigns?: string[]) => useCopilotStore.getState().execute(confirmCallsigns),
  undo: () => useCopilotStore.getState().undo(),
  setActiveProposal: (proposal: StoredProposal | null) =>
    useCopilotStore.getState().setActiveProposal(proposal),
  reset: () => useCopilotStore.getState().reset(),
};

/* Selectors. One question each, so a new chat message does not re-render the
 * review gate and a proposal update does not re-render the message list. */

export function useCopilotMessages(): CopilotMessage[] {
  return useCopilotStore((state) => state.messages);
}

export function useActiveProposal(): StoredProposal | null {
  return useCopilotStore((state) => state.activeProposal);
}

export function useCopilotBusy(): boolean {
  return useCopilotStore((state) => state.busy);
}

export function useCopilotLiveSteps(): CopilotLiveStep[] {
  return useCopilotStore((state) => state.liveSteps);
}

export function useCopilotError(): string | null {
  return useCopilotStore((state) => state.error);
}
